import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import moment from 'moment-timezone';
import { timelineActionTypes } from '@store/Timeline/Timeline.actionTypes';
import { useMemberCompanyTimezones } from '@/hooks/useMemberCompanyTimezones';
import { useMembersOptionsForSelect } from '@/hooks/useMembersOptionsForSelect';
import memberSessionsService from '@/services/memberSessions';
import styles from './Screenshots.module.scss';
import { ReportFiltersBar } from '@/components/ReportFiltersBar';
import { ScreenshotsLightbox } from './ScreenshotsLightbox.component';
import { filterScreenshotsByTimeRange } from './Screenshots.helper';
import { SessionCardComponent as SessionCard } from './components/SessionCard.component';
import { DayGroupHeader } from '@/components/DayGroupHeader';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlTimezoneIgnoreFlagSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@/hooks/useUrlFilterSync';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';
import { collectScreenshotsFromActivities } from '@/utils/screenshotPrivacy';
import { EmptyState } from '@/components/EmptyState';

// Consecutive fetched days with zero sessions before assuming there's no more history.
const EMPTY_DAY_STREAK_LIMIT = 10;
// Hard safety cap on how many days of backward pagination a single date can trigger.
const MAX_LOOKBACK_DAYS = 180;

export const ScreenshotsComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { sessionsByDay, loading, loadingMore, filters } = useSelector(
    (state) => state.timeline
  );
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);

  const teamId = team?.active?.activeTeamid;
  const teamName = team?.active?.Team;
  const companyId = auth?.profile?.activeCompany?.companyId;
  const [selectedMember, setSelectedMember] = useState(null);
  const memberId = selectedMember?.value || null;
  const employeeName = selectedMember?.label?.replace(/\s*\([^)]*\)\s*$/, '');

  const {
    membersOptions,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useMembersOptionsForSelect(teamId);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );

  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  useUrlDateRangeSync({ location, setDateRange: setRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember,
    setSelectedMember,
    currentUserId: auth?.profile?.id,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember,
  });
  useUrlTimezoneIgnoreFlagSync({ history, setIgnoreUrlTimezone });

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  const writeParams = useMemo(() => {
    const params = { start: range.start, end: range.end };
    if (selectedMember?.value) params.member = selectedMember.value;
    if (selectedTimezone) params.tz = selectedTimezone;
    return params;
  }, [range.start, range.end, selectedMember?.value, selectedTimezone]);

  useUrlFilterWrite({
    history,
    location,
    params: writeParams,
    enabled: Boolean(teamId && range?.start && range?.end),
  });

  const onMemberFilterChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMember(opt);
    },
    [setSelectedMember]
  );

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );

  const [sessionScreenshots, setSessionScreenshots] = useState({});
  const [loadingScreenshots, setLoadingScreenshots] = useState({});
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  const [allScreenshotsList, setAllScreenshotsList] = useState([]);
  const [modalOpenToken, setModalOpenToken] = useState(0);
  const [oldestLoadedDate, setOldestLoadedDate] = useState(range.start);
  const [hasMore, setHasMore] = useState(true);

  const sessionScreenshotsRef = useRef(sessionScreenshots);
  const loadingScreenshotsRef = useRef(loadingScreenshots);
  const sessionActivitiesCacheRef = useRef({});
  const hasSeededExpansionRef = useRef(false);
  const isFetchingMoreRef = useRef(false);
  const emptyStreakRef = useRef(0);
  const lookbackDaysRef = useRef(0);
  sessionScreenshotsRef.current = sessionScreenshots;
  loadingScreenshotsRef.current = loadingScreenshots;

  const getSessionActivitiesCacheKey = (sessionId, timezone) =>
    `${sessionId}::${timezone || 'UTC'}`;

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    return match?.employeeId ?? userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: 1,
        pageSize: 1000, // Large page size to get all sessions
        rate: getRateForUser(selectedMember.value),
      },
    });
  };

  // Drop cached/loaded screenshots so expanded sessions re-fetch fresh data.
  const resetLoadedScreenshots = () => {
    setSessionScreenshots({});
    setLoadingScreenshots({});
    setExpandedSessions(new Set());
    sessionActivitiesCacheRef.current = {};
    hasSeededExpansionRef.current = false;
  };

  const handleRefresh = () => {
    resetLoadedScreenshots();
    fetchTimeline();
  };

  useEffect(() => {
    if (selectedMember) {
      // Clear screenshots and expanded sessions when filters change
      resetLoadedScreenshots();
      // Reset backward-pagination state so scrolling starts fresh from the newly selected date
      setOldestLoadedDate(range.start);
      setHasMore(true);
      isFetchingMoreRef.current = false;
      emptyStreakRef.current = 0;
      lookbackDaysRef.current = 0;
      fetchTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  // Fetch the previous day's sessions and append them below what's already loaded
  const loadMorePreviousDay = useCallback(() => {
    if (
      isFetchingMoreRef.current ||
      !hasMore ||
      !selectedMember ||
      !teamId ||
      lookbackDaysRef.current >= MAX_LOOKBACK_DAYS
    ) {
      return;
    }

    const nextDate = moment(oldestLoadedDate, 'YYYY-MM-DD')
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    const employeeId = getEmployeeIdForUser(selectedMember.value);

    isFetchingMoreRef.current = true;
    lookbackDaysRef.current += 1;
    setOldestLoadedDate(nextDate);

    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: nextDate,
        endDate: nextDate,
        timeZone: selectedTimezone,
        mergeMode: 'append',
        rate: getRateForUser(selectedMember.value),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMore,
    oldestLoadedDate,
    selectedMember,
    teamId,
    companyId,
    selectedTimezone,
  ]);

  const wasLoadingMoreRef = useRef(false);
  useEffect(() => {
    if (wasLoadingMoreRef.current && !loadingMore) {
      const loadedDay = (sessionsByDay || []).find(
        (day) => day.dayKey === oldestLoadedDate
      );
      if (loadedDay?.sessions?.length) {
        emptyStreakRef.current = 0;
      } else {
        emptyStreakRef.current += 1;
      }
      if (
        emptyStreakRef.current >= EMPTY_DAY_STREAK_LIMIT ||
        lookbackDaysRef.current >= MAX_LOOKBACK_DAYS
      ) {
        setHasMore(false);
      }
      isFetchingMoreRef.current = false;
    }
    wasLoadingMoreRef.current = !!loadingMore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, sessionsByDay, oldestLoadedDate]);

  // Fetch screenshots for a session
  const fetchSessionScreenshots = async (
    sessionId,
    parentSessionId = null,
    segmentStart = null,
    segmentEnd = null
  ) => {
    const actualSessionId = parentSessionId || sessionId;
    const isSplitSegment = parentSessionId && sessionId !== actualSessionId;
    const shotsMap = sessionScreenshotsRef.current;
    const loadingMap = loadingScreenshotsRef.current;
    const cacheKey = getSessionActivitiesCacheKey(
      actualSessionId,
      selectedTimezone
    );

    if (isSplitSegment && shotsMap[actualSessionId]) {
      const allScreenshots = shotsMap[actualSessionId];
      const filteredScreenshots = filterScreenshotsByTimeRange(
        allScreenshots,
        segmentStart,
        segmentEnd,
        selectedTimezone
      );
      setSessionScreenshots((prev) => ({
        ...prev,
        [sessionId]: filteredScreenshots,
      }));
      return;
    }

    if (shotsMap[actualSessionId] || loadingMap[actualSessionId]) {
      return;
    }

    if (!teamId || !selectedMember?.value || !actualSessionId) {
      return;
    }

    if (sessionActivitiesCacheRef.current[cacheKey]) {
      const cachedAll = sessionActivitiesCacheRef.current[cacheKey];
      setSessionScreenshots((prev) => {
        const updated = { ...prev, [actualSessionId]: cachedAll };
        if (isSplitSegment && segmentStart && segmentEnd) {
          updated[sessionId] = filterScreenshotsByTimeRange(
            cachedAll,
            segmentStart,
            segmentEnd,
            selectedTimezone
          );
        }
        return updated;
      });
      return;
    }

    setLoadingScreenshots((prev) => ({ ...prev, [actualSessionId]: true }));

    let sessionStartTime = null;
    if (sessionsByDay) {
      for (const day of sessionsByDay) {
        const foundSession = day.sessions?.find(
          (s) => s.id === actualSessionId || s.id === sessionId
        );
        if (foundSession?.start) {
          sessionStartTime = foundSession.start;
          break;
        }
      }
    }

    try {
      const employeeId = getEmployeeIdForUser(selectedMember.value);
      const result = await memberSessionsService.getSessionActivities({
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        sessionId: actualSessionId,
        sessionStartTime,
      });

      if (result.success) {
        const activities = result.data.activities || [];
        // Activities arrive oldest-first; show newest screenshots first.
        const allScreenshots =
          collectScreenshotsFromActivities(activities).reverse();

        sessionActivitiesCacheRef.current[cacheKey] = allScreenshots;

        setSessionScreenshots((prev) => {
          const updated = { ...prev, [actualSessionId]: allScreenshots };

          if (isSplitSegment && segmentStart && segmentEnd) {
            const filteredScreenshots = filterScreenshotsByTimeRange(
              allScreenshots,
              segmentStart,
              segmentEnd,
              selectedTimezone
            );
            updated[sessionId] = filteredScreenshots;
          }

          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading session screenshots', error);
    } finally {
      setLoadingScreenshots((prev) => {
        const updated = { ...prev };
        delete updated[actualSessionId];
        if (isSplitSegment) {
          delete updated[sessionId];
        }
        return updated;
      });
    }
  };

  // Fetch screenshots only for sessions whose accordion is open
  useEffect(() => {
    if (loading || !sessionsByDay?.length) return;

    sessionsByDay.forEach((day) => {
      day.sessions.forEach((session) => {
        if (!session.id || !expandedSessions.has(session.id)) return;

        const actualSessionId = session.parentSessionId || session.id;
        const isSplitSegment = session.isSplit && session.parentSessionId;
        const shotsMap = sessionScreenshotsRef.current;
        const loadingMap = loadingScreenshotsRef.current;

        if (!shotsMap[actualSessionId] && !loadingMap[actualSessionId]) {
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            isSplitSegment ? session.start : null,
            isSplitSegment ? session.end : null
          );
        } else if (
          isSplitSegment &&
          !shotsMap[session.id] &&
          shotsMap[actualSessionId]
        ) {
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            session.start,
            session.end
          );
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sessionsByDay, selectedTimezone, expandedSessions]);

  // Open screenshot modal
  const openScreenshotModal = (clickedIndex, screenshots) => {
    const allScreenshots = [];
    sessionsByDay.forEach((day) => {
      day.sessions.forEach((session) => {
        const sessionScreens = sessionScreenshots[session.id] || [];
        sessionScreens.forEach((screenshot) => {
          allScreenshots.push({
            ...screenshot,
            sessionId: session.id,
            dayLabel: day.dayLabel,
          });
        });
      });
    });

    setAllScreenshotsList(allScreenshots);

    const clickedScreenshot = screenshots[clickedIndex];
    const foundIndex = allScreenshots.findIndex(
      (s) =>
        s.url === clickedScreenshot.url &&
        s.timestamp === clickedScreenshot.timestamp &&
        s.activityId === clickedScreenshot.activityId &&
        s.screenIndex === clickedScreenshot.screenIndex
    );

    const idx = foundIndex >= 0 ? foundIndex : 0;

    setSelectedScreenshotIndex(idx);
    setScreenshotModalOpen(true);
    setModalOpenToken((token) => token + 1);
  };

  // Close screenshot modal
  const closeScreenshotModal = () => {
    setScreenshotModalOpen(false);
    setSelectedScreenshotIndex(0);
    setAllScreenshotsList([]);
  };

  const flattenedSessions = useMemo(() => {
    const rows = [];
    (sessionsByDay ?? []).forEach((day) => {
      day.sessions.forEach((session) => {
        rows.push({ dayLabel: day.dayLabel, dayKey: day.dayKey, session });
      });
    });
    return rows;
  }, [sessionsByDay]);

  // Global position of each session across every loaded day, so the bullet
  // color / auto-open-first behavior stays consistent once sessions render
  // grouped under per-day headers instead of one flat list.
  const sessionIndexById = useMemo(() => {
    const map = new Map();
    flattenedSessions.forEach(({ session }, idx) => map.set(session.id, idx));
    return map;
  }, [flattenedSessions]);

  // The first session is auto-opened by the Accordion's isOpenOnStart, but
  // that doesn't call onOpenChange — seed expandedSessions to match so its
  // screenshots actually get fetched and its heading renders as "expanded".
  // Guarded by a ref (reset only on filter change) so a Refresh-triggered
  // sessionsByDay reference change doesn't stomp on a manual collapse.
  useEffect(() => {
    if (!flattenedSessions.length || hasSeededExpansionRef.current) return;
    hasSeededExpansionRef.current = true;
    setExpandedSessions((prev) =>
      prev.size > 0 ? prev : new Set([flattenedSessions[0].session.id])
    );
  }, [flattenedSessions]);

  return (
    <div className={styles.screenshotsPage}>
      <ReportFiltersBar
        membersOptions={membersOptions}
        selectedMember={selectedMember}
        onMemberFilterChange={onMemberFilterChange}
        membersLoading={membersLoading}
        membersError={membersError}
        membersErrorObj={membersErrorObj}
        refetchMembers={refetchMembers}
        timezoneOptions={timezoneOptions}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={handleTimezoneFilterChange}
        timezonesLoading={timezonesLoading}
        date={range.start}
        onDateChange={(dateStr) => setRange({ start: dateStr, end: dateStr })}
        onRefresh={handleRefresh}
        showExport={false}
      />

      {loading && (
        <div className={styles.loaderContainer}>
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading sessions...</p>
        </div>
      )}

      {!loading && !flattenedSessions.length && (
        <EmptyState
          illustration
          title="No Sessions Found"
          description="No sessions found for the selected filters. Try a different date range or employee."
        />
      )}

      {!loading && flattenedSessions.length > 0 && (
        <InfiniteScroll
          dataLength={flattenedSessions.length}
          next={loadMorePreviousDay}
          hasMore={hasMore}
          scrollableTarget="app-scroll-container"
          scrollThreshold={0.9}
          className={styles.sessionsListContainer}
          style={{ overflow: 'visible', width: '100%' }}
          loader={
            loadingMore && (
              <div className={styles.loaderContainer}>
                <Spinner size="sm" color="primary" />
                <p className="mt-3 text-muted">Loading earlier sessions...</p>
              </div>
            )
          }
        >
          {(sessionsByDay ?? []).map((day) => (
            <React.Fragment key={day.dayKey}>
              <DayGroupHeader
                dayKey={day.dayKey}
                dayLabel={day.dayLabel}
                totals={day.totals}
              />
              {day.sessions.map((session) => {
                const index = sessionIndexById.get(session.id) ?? 0;
                return (
                  <SessionCard
                    key={session.id}
                    dayLabel={day.dayLabel}
                    dayKey={day.dayKey}
                    session={session}
                    selectedTimezone={selectedTimezone}
                    screenshots={sessionScreenshots[session.id] || []}
                    isLoadingScreenshots={
                      session.isSplit && session.parentSessionId
                        ? loadingScreenshots[session.parentSessionId] ||
                          loadingScreenshots[session.id]
                        : loadingScreenshots[session.id]
                    }
                    isOpenOnStart={index === 0}
                    isExpanded={expandedSessions.has(session.id)}
                    colorIndex={index}
                    onOpenChange={(open) => {
                      setExpandedSessions((prev) => {
                        const next = new Set(prev);
                        if (open) {
                          next.add(session.id);
                        } else {
                          next.delete(session.id);
                        }
                        return next;
                      });
                    }}
                    onOpenScreenshotModal={(clickedIndex) =>
                      openScreenshotModal(
                        clickedIndex,
                        sessionScreenshots[session.id] || []
                      )
                    }
                  />
                );
              })}
            </React.Fragment>
          ))}
        </InfiniteScroll>
      )}

      <ScreenshotsLightbox
        open={screenshotModalOpen}
        onClose={closeScreenshotModal}
        allScreenshotsList={allScreenshotsList}
        selectedScreenshotIndex={selectedScreenshotIndex}
        setSelectedScreenshotIndex={setSelectedScreenshotIndex}
        selectedTimezone={selectedTimezone}
        employeeName={employeeName}
        teamName={teamName}
        lightboxKey={modalOpenToken}
      />
    </div>
  );
};
